import { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import KPICard from '../../components/common/KPICard';
import { useRouteDetail, useCreateCost, useDeleteCost, useCosts, useCostCategories, useSuppliers } from '../../hooks/useApi';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('da-DK', {
    style: 'currency',
    currency: 'DKK',
    maximumFractionDigits: 0,
  }).format(value);
};

const formatNumber = (value: number, decimals = 0) => {
  return new Intl.NumberFormat('da-DK', {
    maximumFractionDigits: decimals,
  }).format(value);
};

interface RouteDetailPanelProps {
  trailerId: string;
  routeId: string;
  onClose: () => void;
}

export default function RouteDetailPanel({ trailerId, routeId, onClose }: RouteDetailPanelProps) {
  const [costDialogOpen, setCostDialogOpen] = useState(false);
  const [newCost, setNewCost] = useState({
    amount: '',
    description: '',
    category: 'OTHER',
    supplierId: '',
  });

  const { data: detail, isLoading, error } = useRouteDetail(trailerId, routeId);
  const { data: routeCosts } = useCosts({ routeId });
  const { data: categories } = useCostCategories();
  const { data: suppliers } = useSuppliers();
  const createCost = useCreateCost();
  const deleteCost = useDeleteCost();

  const categoryLabels: Record<string, string> = {};
  categories?.forEach(c => { categoryLabels[c.key] = c.name; });

  const supplierLabels: Record<string, string> = {};
  suppliers?.forEach(s => { supplierLabels[s.id] = s.name; });

  const handleAddCost = async () => {
    if (!newCost.amount || !detail) return;
    await createCost.mutateAsync({
      trailerId,
      routeId,
      driverIds: detail.driverIds,
      amount: parseFloat(newCost.amount),
      description: newCost.description,
      category: newCost.category,
      supplierId: newCost.supplierId || null,
      date: detail.date,
    });
    setCostDialogOpen(false);
    setNewCost({ amount: '', description: '', category: 'OTHER', supplierId: '' });
  };

  const handleDeleteCost = async (costId: string) => {
    await deleteCost.mutateAsync(costId);
  };

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">Kunne ikke hente rutedetaljer.</Alert>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!detail) return null;

  return (
    <Box sx={{ p: 2 }}>
      {/* Header with close button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6" fontWeight={700}>
              {detail.firstCity} → {detail.lastCity}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
            <Chip label={detail.date} size="small" variant="outlined" />
            <Chip label={detail.trailerNumber} size="small" color="primary" variant="outlined" />
            <Chip label={detail.driverNames} size="small" variant="outlined" />
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={4}>
          <KPICard title="Omsætning" value={formatCurrency(detail.revenue)} color="primary" />
        </Grid>
        <Grid item xs={4}>
          <KPICard
            title="Omkostninger"
            value={formatCurrency(detail.totalCosts)}
            color="warning"
          />
        </Grid>
        <Grid item xs={4}>
          <KPICard
            title="Profit"
            value={formatCurrency(detail.profit)}
            color={detail.profit >= 0 ? 'success' : 'error'}
          />
        </Grid>
        <Grid item xs={4}>
          <KPICard title="Kilometer" value={`${formatNumber(detail.kilometers)} km`} color="info" />
        </Grid>
        <Grid item xs={4}>
          <KPICard title="Kr/km" value={`${formatNumber(detail.revenuePerKm, 2)} kr`} color="info" />
        </Grid>
        <Grid item xs={4}>
          <KPICard title="Timer" value={formatNumber(detail.hours, 1)} color="info" />
        </Grid>
      </Grid>

      {/* Orders */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Ordrer ({detail.cargos.length})
            </Typography>
          </Box>
          {detail.cargos.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Ref</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Kunde</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Omsætning</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detail.cargos.map((cargo) => (
                    <>
                      <TableRow key={cargo.cargoId} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{cargo.cargoRef}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {cargo.firstCity} → {cargo.lastCity}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                            {cargo.customerName}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={500} color={cargo.revenue > 0 ? 'success.main' : 'text.secondary'}>
                            {formatCurrency(cargo.revenue)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      {/* Invoice lines for this cargo */}
                      {cargo.invoiceLines.map((line) => (
                        <TableRow key={line.id} sx={{ '& td': { py: 0.25, borderBottom: 'none' } }}>
                          <TableCell colSpan={2}>
                            <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                              {line.description}
                              {line.productNumber ? ` (${line.productNumber})` : ''}
                              {line.quantity !== 1 ? ` × ${formatNumber(line.quantity, 2)}` : ''}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="caption" color="text.secondary">
                              {formatCurrency(line.total)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Ingen ordrer på denne rute
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Cost rules breakdown */}
      {detail.costRuleBreakdown.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Omkostningsregler
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  {detail.costRuleBreakdown.map((rule) => (
                    <TableRow key={rule.ruleId} hover>
                      <TableCell>
                        <Typography variant="body2">{rule.ruleName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatNumber(rule.rate, 2)} kr × {formatNumber(rule.units, 2)} {rule.unitLabel}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          {formatCurrency(rule.amount)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>Total regler</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(detail.costRuleCosts)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Manual costs */}
      <Card>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Manuelle omkostninger
            </Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={() => setCostDialogOpen(true)}>
              Tilføj
            </Button>
          </Box>
          {routeCosts && routeCosts.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableBody>
                  {routeCosts.map((cost) => (
                    <TableRow key={cost.id} hover>
                      <TableCell>
                        <Typography variant="body2">{cost.description || categoryLabels[cost.category] || cost.category}</Typography>
                        {cost.supplierId && (
                          <Typography variant="caption" color="text.secondary">
                            {supplierLabels[cost.supplierId] || 'Leverandør'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>{formatCurrency(cost.amount)}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ width: 40 }}>
                        <Tooltip title="Slet">
                          <IconButton size="small" onClick={() => handleDeleteCost(cost.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Ingen manuelle omkostninger
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add Cost Dialog */}
      <Dialog open={costDialogOpen} onClose={() => setCostDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Tilføj ruteomkostning</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Beløb (DKK)"
              type="number"
              value={newCost.amount}
              onChange={(e) => setNewCost({ ...newCost, amount: e.target.value })}
              fullWidth
              autoFocus
            />
            <TextField
              label="Beskrivelse"
              value={newCost.description}
              onChange={(e) => setNewCost({ ...newCost, description: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Kategori</InputLabel>
              <Select
                value={newCost.category}
                label="Kategori"
                onChange={(e) => setNewCost({ ...newCost, category: e.target.value })}
              >
                {categories?.map(cat => (
                  <MenuItem key={cat.key} value={cat.key}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Leverandør (valgfri)</InputLabel>
              <Select
                value={newCost.supplierId}
                label="Leverandør (valgfri)"
                onChange={(e) => setNewCost({ ...newCost, supplierId: e.target.value })}
              >
                <MenuItem value="">
                  <em>Ingen leverandør</em>
                </MenuItem>
                {suppliers?.map(sup => (
                  <MenuItem key={sup.id} value={sup.id}>
                    {sup.name}{sup.cvr ? ` (${sup.cvr})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCostDialogOpen(false)}>Annuller</Button>
          <Button variant="contained" onClick={handleAddCost} disabled={!newCost.amount || createCost.isPending}>
            Tilføj
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
